import { Box, IconButton, TextField, Typography, Paper, Fade, CircularProgress, Avatar } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { chatActions } from "../../redux/slices/chatSlice";
import { startChat } from "../../redux/apiCalls/chatApiCall";
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from "react-i18next";

export default function ChatBot() {
    const dispatch = useDispatch();
    const { messages, loading } = useSelector((state) => state?.chat);

    const { t } = useTranslation('chatBot');

    const [msg, setMsg] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [typingMessage, setTypingMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const lastMessageRef = useRef(null);

    // Auto scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, typingMessage]);

    const lang = localStorage.getItem("lang")
    useEffect(() => {
        // Update greeting when language changes
        const language = localStorage.getItem('lang') || 'en';
        dispatch(chatActions.updateGreetingByLanguage(language));
    }, [lang]); // Or add dependency if language can change

    // Typing effect for AI responses
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];

            if (lastMessage.role === "assistant" && lastMessage !== lastMessageRef.current) {
                lastMessageRef.current = lastMessage;
                setIsTyping(true);
                setTypingMessage("");

                let index = 0;
                const text = lastMessage.content;

                const typeInterval = setInterval(() => {
                    if (index < text.length) {
                        setTypingMessage(text.slice(0, index + 1));
                        index++;
                    } else {
                        setIsTyping(false);
                        clearInterval(typeInterval);
                    }
                }, 20);

                return () => clearInterval(typeInterval);
            }
        }
    }, [messages]);

    const handleSend = async () => {
        if (!msg.trim() || loading) return;

        const newMsg = { role: "user", content: msg };
        const updatedMessages = [...messages, newMsg];

        dispatch(chatActions.addMessage(newMsg));
        dispatch(startChat(updatedMessages));
        setMsg("");
    };


    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClickOutside = (e) => {
        if (chatContainerRef.current && !chatContainerRef.current.contains(e.target)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <>
            {/* Chat Icon Button */}
            {!isOpen && (
                <Fade in={!isOpen}>
                    <IconButton
                        onClick={() => setIsOpen(true)}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            width: 60,
                            height: 60,
                            bgcolor: 'primary.main',
                            color: 'white',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease',
                            zIndex: 1000,
                        }}
                    >
                        <ChatIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                </Fade>
            )}

            {/* Chat Window */}
            {isOpen && (
                <Fade in={isOpen}>
                    <Paper
                        ref={chatContainerRef}
                        elevation={10}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            width: { xs: '90vw', sm: 400 },
                            height: 600,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 3,
                            overflow: 'hidden',
                            zIndex: 1000,
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SmartToyIcon />
                                <Typography variant="h6" fontWeight="bold">
                                    {t("AI Assistant")}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={() => setIsOpen(false)}
                                sx={{
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Messages Container */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                p: 2,
                                bgcolor: '#f5f5f5',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            {messages.map((m, index) => {
                                const isLastAssistant =
                                    m.role === "assistant" &&
                                    index === messages.length - 1 &&
                                    isTyping;

                                const displayContent = isLastAssistant ? typingMessage : m.content;

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: m.role === "user" ? 'flex-end' : 'flex-start',
                                            gap: 1,
                                        }}
                                    >
                                        {m.role === "assistant" && (
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <SmartToyIcon sx={{ fontSize: 18, }} />
                                            </Avatar>
                                        )}
                                        <Box
                                            sx={{
                                                maxWidth: '75%',
                                                bgcolor: m.role === "user" ? 'primary.main' : 'white',
                                                color: m.role === "user" ? 'white' : 'text.primary',
                                                p: 1.5,
                                                borderRadius: 2,
                                                boxShadow: 1,
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                {displayContent}
                                            </Typography>
                                        </Box>
                                        {m.role === "user" && (
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <PersonIcon sx={{ fontSize: 18 }} />
                                            </Avatar>
                                        )}
                                    </Box>
                                );
                            })}

                            {loading && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                        <SmartToyIcon sx={{ fontSize: 18 }} />
                                    </Avatar>
                                    <Box
                                        sx={{
                                            bgcolor: 'white',
                                            p: 2,
                                            borderRadius: 2,
                                            boxShadow: 1,
                                        }}
                                    >
                                        <CircularProgress size={20} />
                                    </Box>
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>

                        {/* Input Area */}
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                gap: 1,
                            }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder={t("Type your message...")}
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading}
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleSend}
                                disabled={!msg.trim() || loading}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                    '&:disabled': {
                                        bgcolor: 'grey.300',
                                    },
                                }}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Paper>
                </Fade>
            )}
        </>
    );
}